import { z } from 'zod';
import { ModelFolderNameSchema } from './models.js';
import { STORAGE_PREFIX } from './storage.js';
import { isStorageObjectKey } from './storage-ops.js';
/** The model library in the user's bucket (doc 75): `couch/models/<folder>/<file>`. */
export const LIBRARY_PREFIX = `${STORAGE_PREFIX}models/`;
export const libraryKey = (folder, fileName) => `${LIBRARY_PREFIX}${folder}/${fileName}`;
/** The folder and file of a library key; null for anything else under couch/models/. */
export function parseLibraryKey(key) {
    if (!key.startsWith(LIBRARY_PREFIX))
        return null;
    const rest = key.slice(LIBRARY_PREFIX.length);
    const cut = rest.lastIndexOf('/');
    if (cut <= 0 || cut === rest.length - 1)
        return null;
    const folder = rest.slice(0, cut);
    return ModelFolderNameSchema.safeParse(folder).success ? { folder, fileName: rest.slice(cut + 1) } : null;
}
/** A plain file name: no folders, not hidden, nothing a storage key could not hold. */
const FileName = z.string().min(1).max(255)
    .refine((n) => !n.includes('/') && !n.startsWith('.') && isStorageObjectKey(`${LIBRARY_PREFIX}${n}`), 'Not a model file name');
export const LibraryModelSchema = z.object({ folder: ModelFolderNameSchema, fileName: z.string(), sizeBytes: z.number().int().min(0), modified: z.string() });
/** GET /api/v1/storage/models — `via` signed the listing: a home machine's name or "couch". */
export const LibraryResponseSchema = z.object({ models: z.array(LibraryModelSchema), via: z.string(), truncated: z.boolean() });
export const LibraryUploadStatusSchema = z.enum(['running', 'uploaded', 'done', 'failed', 'cancelled']);
/** A model on its way to the library: the agent's snapshot plus Couch's last step (complete). */
export const LibraryUploadSchema = z.object({
    id: z.string().max(64),
    machineId: z.string(),
    machineName: z.string(),
    folder: ModelFolderNameSchema,
    fileName: z.string(),
    sizeBytes: z.number().int().min(0),
    sentBytes: z.number().int().min(0),
    status: LibraryUploadStatusSchema,
    error: z.string().nullable(),
});
export const LibraryUploadsResponseSchema = z.object({ uploads: z.array(LibraryUploadSchema) });
/** The agent's own view of an upload (command upload_status). */
export const AgentUploadSchema = z.object({
    id: z.string().max(64),
    folder: z.string(),
    fileName: z.string(),
    sizeBytes: z.number().int().min(0),
    sentBytes: z.number().int().min(0),
    status: z.enum(['running', 'uploaded', 'failed', 'cancelled']),
    error: z.string().nullable(),
    parts: z.array(z.object({ partNumber: z.number().int().min(1), etag: z.string().max(128) })).nullable(),
});
export const AgentUploadsSchema = z.object({ uploads: z.array(AgentUploadSchema) });
/** POST /api/v1/storage/models — "To library" from a machine. */
export const ToLibraryRequestSchema = z.object({ machineId: z.string().min(1).max(64), folder: ModelFolderNameSchema, fileName: FileName });
/** POST /api/v1/machines/:id/library-installs — "Install from library". */
export const LibraryInstallRequestSchema = z.object({ folder: ModelFolderNameSchema, fileName: FileName });
//# sourceMappingURL=storage-library.js.map